import { ReviewCard } from './ReviewCard'
import { RepliesSection } from './RepliesSection'
import {
  useReview,
  useUserLikesForReviews,
  useReviewLikesCounts,
  useToggleReviewLike,
} from '../queries/useReviews'
import { pb } from '../pocketbase/Pocketbase'

interface SingleReviewSectionProps {
  reviewId: string
  highlightReplyId?: string | null
}

export const SingleReviewSection = ({ reviewId, highlightReplyId }: SingleReviewSectionProps) => {
  // Fetch the single review
  const { data: review, isLoading, isError } = useReview(reviewId)
  const { data: likedReviewIds = new Set<string>() } = useUserLikesForReviews([reviewId])
  const { data: likesCounts = new Map<string, number>() } = useReviewLikesCounts([reviewId])
  const toggleLikeMutation = useToggleReviewLike()

  const handleLike = () => {
    if (!pb.authStore.isValid) {
      alert('Please log in to like a review')
      return
    }
    toggleLikeMutation.mutate(reviewId)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 14) return '1 week ago'
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 60) return '1 month ago'
    return `${Math.floor(diffDays / 30)} months ago`
  }

  if (isLoading) {
    return (
      <div className="mb-10">
        <h2 className="text-3xl font-semibold mb-6">Reported Review</h2>
        <div className="text-center text-gray-500 py-8">Loading review...</div>
      </div>
    )
  }

  if (isError || !review) {
    return (
      <div className="mb-10">
        <h2 className="text-3xl font-semibold mb-6">Reported Review</h2>
        <div className="text-center text-red-500 py-8 border border-red-200 rounded-lg">
          <p className="text-lg mb-2">Review not found</p>
          <p className="text-sm">This review may have been deleted.</p>
        </div>
      </div>
    )
  }

  const username = review.expand?.user?.username || 'Anonymous'

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-semibold">Reported Review</h2>
        <span className="text-sm text-gray-500 bg-yellow-100 px-3 py-1 rounded-full">
          Under Review
        </span>
      </div>

      <ReviewCard
        username={username}
        rating={review.rating}
        reviewText={review.text}
        date={formatDate(review.created)}
        likesCount={likesCounts.get(review.id) ?? 0}
        isLiked={likedReviewIds.has(review.id)}
        isOwnReview={review.user === pb.authStore.record?.id}
        onLike={handleLike}
      />

      <RepliesSection reviewId={review.id} highlightReplyId={highlightReplyId} />
    </div>
  )
}
